import { sendbirdSelectors, useSendbirdStateContext } from '@sendbird/uikit-react';
import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { UserInfoResponse } from '@/chat-types/model/user/UserInfoResponse';
import UserChipItem from '@/components/channelList/UserChipItem';
import { KEY } from '@/consts/keys';
import useDebounce from '@/hooks/useDebounce';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import useModalHook from '@/hooks/useModalHook';
import strings from '@/i18n/index';
import { useGetUserListQuery } from '@/queries/hooks/useGetUserListQuery';
import useAuthStore from '@/stores/authStore';
import useChannelListQueryStore from '@/stores/channelListQueryStore';
import useCurrentChannel from '@/stores/currentChannelStore';
import useThemeStore from '@/stores/themeStore';
import useUserListStore from '@/stores/userListStore';

export const useAddChannelModal = () => {
  const { theme } = useThemeStore();
  const { userInfo: loginUserInfo } = useAuthStore();
  const { closeModal } = useModalHook();
  const { showServerError, openErrorAlert } = useErrorHandler();
  const { setCurrentChannel } = useCurrentChannel();

  const scrollRef = useRef<HTMLDivElement>(null);
  const targetObserverRef = useRef<HTMLDivElement>(null);

  const globalStore = useSendbirdStateContext();
  const createChannel = sendbirdSelectors.getCreateGroupChannel(globalStore);

  const [scrollHeight, setScrollHeight] = useState(0);
  const [offsetHeight, setOffsetHeight] = useState(0);

  const {
    records,
    searchName,
    selectedIds,
    selectedUsers,
    setSearchName,
    resetSearchName,
    setSelectedUsers,
    clearState,
  } = useUserListStore();
  const { clearChannelListQuery } = useChannelListQueryStore();
  const debounceSearchValue = useDebounce(searchName, 300);

  const {
    data: userListQuery,
    total,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetUserListQuery({
    records,
    searchName: debounceSearchValue,
  });

  const filteredUserList = useMemo(() => {
    return userListQuery?.filter((user) => user.userId !== loginUserInfo?.userId);
  }, [userListQuery, loginUserInfo?.userId]);

  useEffect(() => {
    setTimeout(() => {
      setScrollHeight(scrollRef.current?.scrollHeight || 0);
      setOffsetHeight(scrollRef.current?.offsetHeight || 0);
    }, 300);
    // eslint-disable-next-line
    }, [scrollRef, scrollHeight, offsetHeight]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [scrollRef, scrollRef.current, searchName]);

  const resetLabel = useMemo(() => {
    const selectedText = strings.SENDBIRD.MODAL__CREATE_CHANNEL__SELECTED;
    return `<span>${selectedIds.length}</span> / ${total} ${selectedText}`;
  }, [selectedIds, total]);

  const onIntersect = async (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
    const entry = entries[0];
    if (entry.isIntersecting && hasNextPage) {
      observer.unobserve(entry.target);
      if (hasNextPage) {
        await fetchNextPage();
      }
      observer.observe(entry.target);
    }
  };

  useIntersectionObserver(targetObserverRef, onIntersect, {
    threshold: 1.0,
  });

  const closeModalHandler = () => {
    clearState(); // state clear
    closeModal('addChannelModal');
  };

  const confirmModalHandler = async () => {
    try {
      // selectedIds 가 0명이면 오류 팝업
      if (selectedIds.length === 0) {
        openErrorAlert({
          depth: 2,
          message: strings.ALERT__NO_ONE_SELECTED,
        });
        return;
      }

      // selectedIds 가 20,000 이상이면 오류 팝업
      if (selectedIds.length > 20000) {
        openErrorAlert({
          depth: 2,
          message: strings.ALERT__MAX_INVITE_MEMBER,
        });
        return;
      }

      const createParams = {
        invitedUserIds: selectedIds,
        isPublic: false,
        isEphemeral: false,
        isDistinct: selectedIds.length === 1,
        isStrict: true,
        customType: localStorage.getItem(KEY.CLIENT_ID) ?? '',
      };

      createChannel(createParams)
        .then((res) => {
          setCurrentChannel(res);
          closeModalHandler();
          clearChannelListQuery();
        })
        .catch((err) => {
          if (err.message.includes('User id is not valid')) {
            openErrorAlert({
              depth: 2,
              message: `<p>${strings.MSG__.ERROR__.CREATE_CHANNEL}</p><span>${err.message}</span>`,
            });
            return;
          }
          showServerError();
          return;
        });
    } catch (error) {
      showServerError();
      return;
    }
  };

  const changeInputHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchName(e.target.value);
  };

  const selectTargetUserHandler = (selected: UserInfoResponse) => {
    if (selectedIds.includes(selected.userId)) {
      // MARK 동일 id 존재할 때,
      setSelectedUsers(selectedUsers.filter((origin) => origin.userId !== selected.userId));
      return;
    }
    setSelectedUsers([...selectedUsers, selected]);
  };

  const resetInputHandler = () => resetSearchName();
  const resetTargetUserHandler = () => clearState();

  const renderSelectedUserChips = (renderList: UserInfoResponse[]) => {
    return (
      renderList &&
      renderList.map((user, index) => (
        <UserChipItem
          key={`chips__${user.userId}${index}`}
          user={user}
          index={index}
          onClickDelete={() => {
            selectTargetUserHandler(user);
          }}
        />
      ))
    );
  };

  return {
    theme,
    userList: filteredUserList,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    searchName,
    selectedIds,
    selectedUsers,
    resetLabel,
    hasScroll: scrollHeight > offsetHeight,
    scrollRef,
    targetObserverRef,
    closeModalHandler,
    confirmModalHandler,
    changeInputHandler,
    resetInputHandler,
    selectTargetUserHandler,
    resetTargetUserHandler,
    renderSelectedUserChips,
  };
};
